import {
  useState,
  useCallback,
  useEffect,
  useId,
  useRef,
  createContext,
  useContext,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { CheckCircle, AlertCircle, X, Info } from "lucide-react";
import { isTopmostDialog, openDialogStack, pushDialog, removeDialog } from "../../lib/dialogStack";

// Toast Types
export type ToastType = "success" | "error" | "info" | "warning";

/**
 * One action offered from inside a toast.
 *
 * Added for the P&L clear (20260915000300): the person who needs to undo a
 * clear is almost always the person who just did it, still looking at the
 * project they did it on, so the shortest route back has to be on the
 * confirmation of the act itself rather than on an admin page they would have
 * to know exists. Optional, so every existing caller is unchanged.
 */
export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface Toast {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
  duration?: number;
  action?: ToastAction;
}

interface ToastContextType {
  toasts: Toast[];
  addToast: (toast: Omit<Toast, "id">) => void;
  removeToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const addToast = useCallback((toast: Omit<Toast, "id">) => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const newToast = { ...toast, id };

    setToasts((prev) => [...prev, newToast]);

    // Auto-remove after duration (default 5 seconds)
    const duration = toast.duration ?? 5000;
    if (duration > 0) {
      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
      }, duration);
    }
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast }}>
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </ToastContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components -- auto-suppressed for safety
export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
}

// Toast Container Component
function ToastContainer({
  toasts,
  onRemove,
}: {
  toasts: Toast[];
  onRemove: (id: string) => void;
}) {
  if (toasts.length === 0) return null;

  return createPortal(
    <div
      role="status"
      aria-live="polite"
      style={{
        position: "fixed",
        bottom: 20,
        right: 20,
        zIndex: 2147483647,
        display: "flex",
        flexDirection: "column",
        gap: 12,
        maxWidth: 400,
        pointerEvents: "none",
      }}
    >
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onRemove={onRemove} />
      ))}
    </div>,
    document.body,
  );
}

// Single Toast Item
function ToastItem({
  toast,
  onRemove,
}: {
  toast: Toast;
  onRemove: (id: string) => void;
}) {
  const getStyles = () => {
    switch (toast.type) {
      case "success":
        return {
          bg: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
          icon: <CheckCircle size={20} />,
        };
      case "error":
        return {
          bg: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
          icon: <AlertCircle size={20} />,
        };
      case "warning":
        return {
          // SEQ-226: solid darker amber so white title and body clear 4.5:1.
          bg: "var(--toast-warning-bg)",
          icon: <AlertCircle size={20} />,
        };
      case "info":
      default:
        return {
          bg: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
          icon: <Info size={20} />,
        };
    }
  };

  const styles = getStyles();

  return (
    <div
      role={toast.type === "error" ? "alert" : undefined}
      style={{
        background: styles.bg,
        color: "white",
        padding: "14px 16px",
        borderRadius: 12,
        boxShadow: "0 10px 25px rgba(0,0,0,0.2)",
        display: "flex",
        alignItems: "flex-start",
        gap: 12,
        animation: "slideIn 0.3s ease-out",
        pointerEvents: "auto",
      }}
    >
      <div style={{ flexShrink: 0, marginTop: 2 }}>{styles.icon}</div>
      <div style={{ flex: 1, minWidth: 0, overflowWrap: "anywhere" }}>
        <div style={{ fontWeight: 600, marginBottom: toast.message ? 4 : 0 }}>
          {toast.title}
        </div>
        {toast.message && (
          <div
            style={{ fontSize: 13, opacity: toast.type === "warning" ? 1 : 0.9 }}
          >
            {toast.message}
          </div>
        )}
        {toast.action && (
          <button
            type="button"
            onClick={() => {
              toast.action?.onClick();
              onRemove(toast.id);
            }}
            style={{
              marginTop: 10,
              minHeight: 36,
              padding: "0 14px",
              background: "rgba(255,255,255,0.22)",
              border: "1px solid rgba(255,255,255,0.45)",
              borderRadius: 8,
              color: "white",
              font: "inherit",
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            {toast.action.label}
          </button>
        )}
      </div>
      <button
        type="button"
        onClick={() => onRemove(toast.id)}
        aria-label="Dismiss notification"
        style={{
          background: "rgba(255,255,255,0.2)",
          border: "none",
          borderRadius: 6,
          padding: 4,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "white",
        }}
      >
        <X size={16} />
      </button>
    </div>
  );
}

// Modal Component
interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm?: () => void; // Optional because sometime we use children for actions
  title?: ReactNode; // Optional if we want custom header
  children: ReactNode;
  confirmText?: string;
  cancelText?: string;
  confirmDisabled?: boolean;
  size?: "sm" | "md" | "lg" | "xl" | "full";
  maxWidth?: string | number;
  hideFooter?: boolean;
  /**
   * An accessible name for the dialog, needed only when `title` is a
   * ReactNode rather than a string. A string title is used automatically.
   */
  ariaLabel?: string;
}

/**
 * SEQ-180 phase 2 (React review, HIGH). This was a plain div with a
 * click-outside backdrop: no dialog role, no accessible name, no Escape, no
 * focus management. Every consumer inherited that, including the drill-down
 * panel and the dashboard bar's four dialogs, so it is fixed here once rather
 * than per caller. The props and the markup are unchanged; only the
 * behaviour and the ARIA are added, plus one optional `ariaLabel` for a
 * caller whose `title` is a ReactNode.
 *
 * Stacking (SEQ-180 phase 2, operational metric delete). Both handlers below
 * listen on `document`, so with two dialogs open at once every one of them
 * fired: Escape on a confirm dialog closed the editor underneath it too,
 * losing unsaved edits, and the outer dialog's Tab trap fought the inner
 * one's. `openDialogStack` makes both handlers no-ops unless this dialog is
 * the topmost open one. Nothing changes for a single dialog, which is every
 * existing caller.
 *
 * Effect identity (React review round 3, HIGH). The effect deliberately
 * depends on `isOpen` and `dialogId` only, never on `onClose`. No caller in
 * this codebase memoises `onClose`; every one is a fresh inline arrow, so
 * listing it would tear the effect down and set it up again on every
 * re-render of the owning component while the dialog stayed open. Two things
 * broke because of that, one of them proven: the cleanup restored focus to
 * whatever held it when the dialog first opened, so a state change during an
 * async action (the metric delete sets a saving flag mid-flight) yanked focus
 * off the button the user was looking at; and a re-push could land an outer
 * dialog after an already-open inner one in the stack, handing Escape to the
 * editor underneath, which is the very bug the stack exists to prevent. The
 * effect now runs only on a genuine open or close, and `onCloseRef` carries
 * the latest callback so nothing goes stale.
 */
export function Modal({
  isOpen,
  onClose,
  onConfirm,
  title,
  children,
  confirmText = "Confirm",
  cancelText = "Cancel",
  confirmDisabled = false,
  size = "md",
  maxWidth,
  hideFooter = false,
  ariaLabel,
}: ModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const dialogId = useId();

  // Always-latest callback ref. Read only from inside the keydown handler,
  // which is registered once per genuine open, so it must never close over a
  // stale `onClose` and must never be a reason to re-register.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  // Hooks run before the `isOpen` early return below, because a hook cannot
  // be conditional. The effect itself does nothing while the modal is shut.
  useEffect(() => {
    if (!isOpen) return;

    pushDialog(openDialogStack, dialogId);

    const returnFocusTo = document.activeElement;
    const focusFrame = requestAnimationFrame(() => {
      const dialog = dialogRef.current;
      if (!dialog) return;
      // Do not steal focus from a field the content deliberately autoFocused
      // (the P&L cell editor puts the caret in its number input so a user can
      // type a value straight away). Only pull focus in when it is still
      // outside the dialog, which is the case this exists for.
      if (dialog.contains(document.activeElement)) return;
      dialog.focus();
    });

    const onKeyDown = (e: KeyboardEvent) => {
      // A dialog underneath another one ignores the keyboard entirely: the
      // one on top owns both Escape and the Tab trap.
      if (!isTopmostDialog(openDialogStack, dialogId)) return;
      if (e.key === "Escape") {
        onCloseRef.current();
        return;
      }
      if (e.key !== "Tab" || !dialogRef.current) return;

      const focusable = dialogRef.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => {
      cancelAnimationFrame(focusFrame);
      document.removeEventListener("keydown", onKeyDown);
      removeDialog(openDialogStack, dialogId);
      // Safe to restore unconditionally only because this cleanup now runs
      // on a genuine close or unmount, never on an unrelated re-render.
      if (returnFocusTo instanceof HTMLElement) returnFocusTo.focus();
    };
    // These two are the whole dependency list, and exhaustive-deps agrees:
    // the effect reads `onClose` only through `onCloseRef`, so there is
    // nothing to suppress. See the stacking note on this component for why
    // adding `onClose` back would be a regression rather than a tidy-up.
  }, [isOpen, dialogId]);

  if (!isOpen) return null;

  const widths = { sm: 400, md: 550, lg: 700, xl: 1100, full: "95vw" };
  const hasStringTitle = typeof title === "string";

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9998,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
    >
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: "absolute",
          inset: 0,
          background: "rgba(0, 0, 0, 0.5)",
          backdropFilter: "blur(4px)",
        }}
      />

      {/* Modal Content */}
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={hasStringTitle ? titleId : undefined}
        aria-label={hasStringTitle ? undefined : (ariaLabel ?? "Dialog")}
        tabIndex={-1}
        style={{
          position: "relative",
          background: "white",
          borderRadius: 16,
          boxShadow: "0 25px 50px rgba(0,0,0,0.25)",
          width: "100%",
          maxWidth: maxWidth || widths[size],
          maxHeight: "80vh",
          display: "flex",
          flexDirection: "column",
          animation: "modalSlideIn 0.2s ease-out",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "20px 24px",
            borderBottom: "1px solid #e5e7eb",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {hasStringTitle ? (
            <h3 id={titleId} style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>
              {title}
            </h3>
          ) : (
            title
          )}
          <button
            type="button"
            onClick={onClose}
            aria-label="Close dialog"
            style={{
              background: "#f3f4f6",
              border: "none",
              borderRadius: 8,
              padding: 8,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>

        {/* Body */}
        <div
          style={{
            padding: 24,
            overflowY: "auto",
            flex: 1,
          }}
        >
          {children}
        </div>

        {/* Footer */}
        {!hideFooter && (
          <div
            style={{
              padding: "16px 24px",
              borderTop: "1px solid #e5e7eb",
              display: "flex",
              justifyContent: "flex-end",
              gap: 12,
            }}
          >
            {cancelText && (
              <button className="btn btn-secondary" onClick={onClose}>
                {cancelText}
              </button>
            )}
            <button
              className="btn btn-primary"
              onClick={onConfirm}
              disabled={confirmDisabled}
            >
              {confirmText}
            </button>
          </div>
        )}
      </div>

      <style>{`
        @keyframes modalSlideIn {
          from {
            opacity: 0;
            transform: scale(0.95) translateY(-10px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateX(100%);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
      `}</style>
    </div>
  );
}
